import { useState } from 'react';
import { Send, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { enquiriesApi, ctaApi } from '../../services/api';

export const EnquiryForm = ({ projectName = '', compact = false }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    project: projectName,
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await enquiriesApi.submit({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        project: formData.project,
        message: formData.message,
        source: 'Contact Form',
      });
      // Track CTA event
      try { await ctaApi.track('Enquiry Form Submit', compact ? 'Homepage CTA' : 'Contact Page'); } catch {}
      setIsSubmitted(true);
      toast.success('Thank you for your enquiry! We will contact you shortly.');
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', phone: '', project: projectName, message: '' });
      }, 3000);
    } catch (err) {
      toast.error(err.message || 'Failed to send enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (isSubmitted) {
    return (
      <div
        data-testid="enquiry-success"
        className="flex flex-col items-center justify-center py-12 text-center"
      >
        <CheckCircle className="w-16 h-16 text-emerald-500 mb-4" />
        <h3 className="text-xl font-semibold text-slate-900 font-outfit mb-2">
          Thank You!
        </h3>
        <p className="text-slate-600 font-manrope">
          We've received your enquiry and will get back to you soon.
        </p>
      </div>
    );
  }

  if (compact) {
    return (
      <form onSubmit={handleSubmit} data-testid="enquiry-form-compact" className="space-y-4">
        <Input
          name="name"
          placeholder="Your Name *"
          value={formData.name}
          onChange={handleChange}
          required
          data-testid="enquiry-name"
          className="bg-slate-50 border-slate-200"
        />
        <Input
          name="phone"
          type="tel"
          placeholder="Phone Number *"
          value={formData.phone}
          onChange={handleChange}
          required
          data-testid="enquiry-phone"
          className="bg-slate-50 border-slate-200"
        />
        <Input
          name="email"
          type="email"
          placeholder="Email Address"
          value={formData.email}
          onChange={handleChange}
          data-testid="enquiry-email"
          className="bg-slate-50 border-slate-200"
        />
        <Button
          type="submit"
          disabled={submitting}
          data-testid="enquiry-submit"
          className="w-full bg-[#47ace2] hover:bg-[#1f7db3] text-white rounded-full"
        >
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          {submitting ? 'Sending...' : 'Send Enquiry'}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="enquiry-form" className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Input
          name="name"
          placeholder="Your Name *"
          value={formData.name}
          onChange={handleChange}
          required
          data-testid="enquiry-name"
          className="bg-slate-50 border-slate-200 rounded-xl px-4 py-3"
        />
        <Input
          name="phone"
          type="tel"
          placeholder="Phone Number *"
          value={formData.phone}
          onChange={handleChange}
          required
          data-testid="enquiry-phone"
          className="bg-slate-50 border-slate-200 rounded-xl px-4 py-3"
        />
      </div>
      <Input
        name="email"
        type="email"
        placeholder="Email Address *"
        value={formData.email}
        onChange={handleChange}
        required
        data-testid="enquiry-email"
        className="bg-slate-50 border-slate-200 rounded-xl px-4 py-3"
      />
      {!projectName && (
        <Select
          value={formData.project}
          onValueChange={(v) => setFormData((prev) => ({ ...prev, project: v }))}
        >
          <SelectTrigger
            data-testid="enquiry-project"
            className="bg-slate-50 border-slate-200 rounded-xl px-4 py-3"
          >
            <SelectValue placeholder="Select Project (Optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="general">General Enquiry</SelectItem>
            <SelectItem value="modern-arch-heights">Modern Arch Heights</SelectItem>
            <SelectItem value="modern-embassy-towers">Modern Embassy Towers</SelectItem>
            <SelectItem value="modern-villa-enclave">Modern Villa Enclave</SelectItem>
            <SelectItem value="modern-skyline-residency">Modern Skyline Residency</SelectItem>
            <SelectItem value="modern-business-park">Modern Business Park</SelectItem>
          </SelectContent>
        </Select>
      )}
      <Textarea
        name="message"
        placeholder="Your Message"
        value={formData.message}
        onChange={handleChange}
        rows={4}
        data-testid="enquiry-message"
        className="bg-slate-50 border-slate-200 rounded-xl px-4 py-3 resize-none"
      />
      <Button
        type="submit"
        disabled={submitting}
        data-testid="enquiry-submit"
        className="w-full bg-[#47ace2] hover:bg-[#1f7db3] text-white rounded-full py-6 text-lg"
      >
        {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}
        {submitting ? 'Sending...' : 'Send Enquiry'}
      </Button>
    </form>
  );
};
